import type {
  InsightDto,
  InsightIconKey,
} from "../../types/weekly-cost-report.types";
import { ConversionToK } from "../shared/costCalculations.utils";
import { getInsightIcon } from "./weeklyCostInsightIcon.utils";

const INSIGHT_BORDER_COLORS: Record<InsightIconKey, string> = {
  subscription: "var(--text-internal)",
  service: "var(--wcr-insight-service-border)",
  growth: "var(--wcr-insight-growth-border)",
  trend: "var(--wcr-insight-growth-border)",
  peak: "var(--wcr-insight-peak-border)",
  forecast: "var(--wcr-insight-forecast-border)",
};

type InsightFormattingOptions = {
  customerName?: string | null;
  index?: number;
};

export type FormattedWeeklyCostInsight = {
  iconClassName: string;
  borderColor: string;
  label: string;
  entityName: string | null;
  spendText: string;
  shareText: string | null;
  trendIconClassName: string | null;
  trendClassName: string;
  trendText: string | null;
};

/**
 * Derive trend presentation details (icon class, CSS class, and text) from change percent/context.
 * - returns null icon when changePercent is null/undefined
 * - returns 'minus' icon and "No change" text when changePercent is 0
 * - otherwise returns up/down icon and formatted percent text
 */
const getTrendDetails = (
  changePercent?: number | null,
  changeContext?: string,
): Pick<
  FormattedWeeklyCostInsight,
  "trendIconClassName" | "trendClassName" | "trendText"
> => {
  if (changePercent === undefined || changePercent === null) {
    return {
      trendIconClassName: null,
      trendClassName: "text-[var(--text-tertiary)]",
      trendText: null,
    };
  }

  if (changePercent === 0) {
    return {
      trendIconClassName: "pi pi-minus",
      trendClassName: "text-[var(--text-tertiary)]",
      trendText: `No change${changeContext ? ` ${changeContext}` : ""}`,
    };
  }

  return {
    trendIconClassName:
      changePercent > 0 ? "pi pi-arrow-up" : "pi pi-arrow-down",
    trendClassName:
      changePercent > 0
        ? "text-[var(--wcr-insight-trend-positive)]"
        : "text-[var(--wcr-insight-trend-negative)]",
    trendText: `${Math.abs(changePercent).toFixed(1)}% ${changeContext ?? "vs last week"}`,
  };
};

/**
 * Format a raw Insight DTO into a display-friendly object for the weekly cost insights UI.
 * - builds icon class, border color, label, entity name, formatted spend and share text
 * - delegates to getTrendDetails for trend-related fields
 * @param {InsightDto} insight - raw insight data from API
 * @param {InsightFormattingOptions} [options] - optional formatting options (customerName, index)
 * @returns {FormattedWeeklyCostInsight}
 */
export const formatWeeklyCostInsight = (
  insight: InsightDto,
  options?: InsightFormattingOptions,
): FormattedWeeklyCostInsight => {
  const customerName = options?.customerName?.trim();
  const isCustomerScoped = Boolean(customerName);
  const entityName =
    insight.entityName?.trim() ||
    (isCustomerScoped && options?.index === 0 ? customerName || null : null);
  const shareText =
    insight.sharePercent === undefined || insight.sharePercent === null
      ? null
      : insight.iconKey === "subscription" && isCustomerScoped
        ? `${insight.sharePercent.toFixed(1)}%`
        : `${insight.sharePercent.toFixed(1)}%`;

  let label = insight.label;
  if (isCustomerScoped && label.startsWith("Top subscription")) {
    label = label.substring(4);
    label = label.charAt(0).toUpperCase() + label.slice(1);
  }

  const borderColor = INSIGHT_BORDER_COLORS[insight.iconKey];

  return {
    iconClassName: getInsightIcon(insight.iconKey),
    borderColor,
    label,
    entityName,
    spendText: `$${ConversionToK(insight.spend)}`,
    shareText,
    ...getTrendDetails(insight.changePercent, insight.changeContext),
  };
};
